import { useEffect, useMemo, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { useCallbackRef } from '../../../hooks/useCallbackRef';
import { useSyncedRef } from '../../../hooks/useSyncedRef';
import { ButtonVariants } from '../../Button';
import { HStack } from '../../Core/HStack';
import { NumberInput } from '../NumberInput';
import { SearchSelect } from '../SearchSelect/SearchSelect';
import { ToggleGroup, type ToggleGroupOption } from '../ToggleGroup';
import { FormControlSizes } from '../types';

const allTimeRangeValues = [
  { value: 'H', label: 'hours' },
  { value: 'D', label: 'days' },
  { value: 'W', label: 'weeks' },
  { value: 'M', label: 'months' },
  { value: 'Y', label: 'years' },
] as const satisfies TimeValue[];

/** The value used to indicate a custom term in DurationInput. */
export const DURATION_INPUT_CUSTOM_VALUE = 'CUSTOM';

const termOptions = [
  { displayName: '1W', value: '1W' },
  { displayName: '1M', value: '1M' },
  { displayName: '3M', value: '3M' },
  { displayName: '6M', value: '6M' },
  { displayName: '9M', value: '9M' },
  { displayName: '1Y', value: '1Y' },
  { displayName: 'Custom', value: DURATION_INPUT_CUSTOM_VALUE },
] as const satisfies ToggleGroupOption<string>[];

const termOptionValues = termOptions.map(option => option.value);

/** Preset chips (excludes Custom); always valid for display/sync regardless of `timeRangeUnits`. */
const fixedTermPresetSet = new Set<string>(
  termOptions.map(o => o.value).filter(v => v !== DURATION_INPUT_CUSTOM_VALUE)
);

function isFixedTermPreset(value: string | undefined): boolean {
  return value != null && fixedTermPresetSet.has(value);
}

export interface DurationInputProps {
  /**
   * The initial value of the duration (e.g., '2M', '4W', undefined).
   * When programmatically changing this value (e.g. template switch, form reset), pass a new `key` to
   * the component so it remounts and stays in sync. Otherwise, if the user is on Custom, the UI may
   * show stale values.
   */
  initialValue: string | undefined;
  /** The function to call when the duration changes */
  onChange: (value: string | undefined) => void;
  /** The placeholder text to display when the user is entering a custom duration. */
  placeholder?: string;
  /** The data-testid attribute to apply to the component */
  dataTestId?: string;
  /** Whether to hide the term selector toggle and show only the custom input */
  hideTermSelector?: boolean;
  /** The prefix to display before the number input */
  prefix?: React.ReactNode;
  // cspell: disable-next-line
  /** Subset of H,D,W,M,Y for the custom unit dropdown and custom validation. Default `DWM` = days, weeks, months. Fixed presets (1W–1Y) ignore this. */
  timeRangeUnits?: string;
  /** The default unit to use when initialValue is undefined (e.g., 'H' for hours, 'D' for days). Must be one of the allowed timeRangeUnits. */
  defaultUnit?: string;
  /** Whether the component is disabled */
  disabled?: boolean;
}

type TimeValue = { value: string; label: string };

function validateValue(
  value: string | undefined,
  allowedUnits: readonly TimeValue[]
): {
  fullValue: string | undefined;
  value: string | undefined;
  units: TimeValue | undefined;
} {
  if (!value) {
    return { fullValue: undefined, value: undefined, units: undefined };
  }
  const [_fullValue, currentTermValue, currentTermUnits] = value?.match(/^(\d+)([A-Z])$/) ?? [];
  if (currentTermValue && currentTermUnits) {
    let isValid = true;
    const foundUnit = allowedUnits.find(option => option.value === currentTermUnits);
    if (!foundUnit) {
      isValid = false;
    }
    if (!Number.isInteger(Number(currentTermValue))) {
      isValid = false;
    }
    if (isValid) {
      return {
        fullValue: value,
        value: currentTermValue,
        units: foundUnit,
      };
    }
  }
  return {
    fullValue: undefined,
    value: undefined,
    units: undefined,
  };
}

/**
 * A component that allows the user to select a duration from a list of options or enter a custom duration.
 */
export const DurationInput = ({
  initialValue,
  onChange,
  placeholder,
  dataTestId,
  hideTermSelector,
  prefix,
  // cspell: disable-next-line
  timeRangeUnits = 'DWM',
  defaultUnit,
  disabled,
}: DurationInputProps) => {
  const filteredTimeRangeValues = useMemo(
    () => allTimeRangeValues.filter(unit => timeRangeUnits.includes(unit.value)),
    [timeRangeUnits]
  );

  // Find the default unit from filteredTimeRangeValues if provided
  const defaultUnitValue = useMemo(() => {
    if (defaultUnit) {
      return filteredTimeRangeValues.find(unit => unit.value === defaultUnit);
    }
    return undefined;
  }, [defaultUnit, filteredTimeRangeValues]);

  const [toggleValue, setToggleValue] = useState<string | undefined>(
    hideTermSelector ? DURATION_INPUT_CUSTOM_VALUE : undefined
  );
  const [customValue, setCustomValue] = useState<string | undefined>(undefined);
  const [customUnits, setCustomUnits] = useState<TimeValue | undefined>(defaultUnitValue);

  const toggleValueRef = useSyncedRef(toggleValue);
  const customValueRef = useSyncedRef(customValue);
  // Ref needed in sync effect: toggleValue omitted from deps to avoid overwriting user's Custom selection
  // when initialValue changes; ref provides current value without causing effect re-runs.
  // customValueRef: read latest numeric input when initialValue syncs (e.g. parent undefined after clear)
  // without listing customValue in deps (would re-run this effect on every keystroke).

  // Sync internal state from initialValue (controlled)
  useEffect(() => {
    if (!hideTermSelector && isFixedTermPreset(initialValue)) {
      if (toggleValueRef.current === DURATION_INPUT_CUSTOM_VALUE) {
        return;
      }
      setToggleValue(initialValue);
      setCustomValue(undefined);
      setCustomUnits(undefined);
      return;
    }

    const validatedValue = validateValue(initialValue, filteredTimeRangeValues);
    if (validatedValue.fullValue) {
      if (
        !hideTermSelector &&
        termOptionValues.includes(validatedValue.fullValue as (typeof termOptionValues)[number])
      ) {
        if (toggleValueRef.current === DURATION_INPUT_CUSTOM_VALUE) {
          return;
        }
        setToggleValue(validatedValue.fullValue);
        setCustomValue(undefined);
        setCustomUnits(undefined);
      } else {
        setToggleValue(DURATION_INPUT_CUSTOM_VALUE);
        setCustomValue(validatedValue.value);
        setCustomUnits(validatedValue.units);
      }
    } else {
      const currentToggle = toggleValueRef.current;
      // With hideTermSelector (e.g. EntityAdmin), preserve number+units UI while the user clears the
      // numeric field (parent value becomes undefined). Still reset when internal value is non-empty
      // so external/programmatic clears (e.g. new entity) do not leave stale digits.
      const cv = customValueRef.current;
      const preservingCustom =
        currentToggle === DURATION_INPUT_CUSTOM_VALUE && (!hideTermSelector || cv === '' || cv === undefined);
      const nextToggle = hideTermSelector
        ? DURATION_INPUT_CUSTOM_VALUE
        : currentToggle === DURATION_INPUT_CUSTOM_VALUE
          ? DURATION_INPUT_CUSTOM_VALUE
          : undefined;
      setToggleValue(nextToggle);
      if (!preservingCustom) {
        setCustomValue(undefined);
        setCustomUnits(defaultUnit ? (defaultUnitValue ?? filteredTimeRangeValues[0]) : undefined);
      }
    }
  }, [
    initialValue,
    hideTermSelector,
    filteredTimeRangeValues,
    defaultUnitValue,
    defaultUnit,
    toggleValueRef,
    customValueRef,
  ]);

  const isCustomMode = toggleValue === DURATION_INPUT_CUSTOM_VALUE || hideTermSelector;

  // When switching from preset to Custom, extract value for display (only if that unit exists in the custom dropdown).
  useEffect(() => {
    if (toggleValue !== DURATION_INPUT_CUSTOM_VALUE || !initialValue || hideTermSelector) {
      return;
    }

    const validatedValue = validateValue(
      initialValue,
      isFixedTermPreset(initialValue) ? allTimeRangeValues : filteredTimeRangeValues
    );
    const isChipPreset =
      !!validatedValue.fullValue &&
      termOptionValues.includes(validatedValue.fullValue as (typeof termOptionValues)[number]);

    if (!isChipPreset) {
      return;
    }

    const units = validatedValue.units;
    const unitAllowed = units != null && filteredTimeRangeValues.some(u => u.value === units.value);

    if (!unitAllowed) {
      setCustomValue(undefined);
      setCustomUnits(undefined);
      return;
    }

    if (customValue) {
      return;
    }

    setCustomValue(validatedValue.value);
    setCustomUnits(units);
  }, [toggleValue, initialValue, hideTermSelector, filteredTimeRangeValues, customValue]);

  const onChangeRef = useCallbackRef(onChange);
  useUpdateEffect(() => {
    if (isCustomMode) {
      const str = `${customValue ?? ''}${customUnits?.value ?? ''}`;
      const validatedValue = validateValue(str || undefined, filteredTimeRangeValues);
      if (validatedValue.fullValue) {
        onChangeRef(validatedValue.fullValue);
      } else if (!customValue || String(customValue).trim() === '') {
        onChangeRef(undefined);
      }
    } else {
      onChangeRef(toggleValue);
    }
  }, [customUnits?.value, customValue, isCustomMode, onChangeRef, toggleValue, filteredTimeRangeValues]);

  return (
    <HStack gap="spacing4" w="100%" data-testid={dataTestId}>
      {!hideTermSelector && (
        <ToggleGroup
          size={FormControlSizes.Small}
          options={termOptions}
          value={toggleValue}
          onChange={setToggleValue}
          boxProps={{ w: '55%' }}
          disabled={disabled}
          data-testid="term-selector"
          defaultSelectedVariant={ButtonVariants.Priority}
        />
      )}
      <NumberInput
        data-testid="custom-term-input"
        placeholder={placeholder}
        width={hideTermSelector ? '100%' : '45%'}
        integerOnly
        value={customValue ?? ''}
        disabled={!isCustomMode || disabled}
        onChange={setCustomValue}
        minIncrement="1"
        defaultIncrement="1"
        largeIncrement="1"
        prefix={prefix}
        suffix={
          <SearchSelect
            data-testid="custom-term-units"
            options={filteredTimeRangeValues}
            selection={customUnits}
            disabled={!isCustomMode || disabled}
            onChange={setCustomUnits}
            getLabel={option => option.label}
            placeholder="Unit"
            w="100px"
            size={FormControlSizes.Small}
            initialSortByLabel={false}
          />
        }
      />
    </HStack>
  );
};
